import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { MultiSelectFilter } from "../../../../../ui/MultiSelectFilter";
import { useRestaurantStore } from "../../../../../../stores/appRestaurantStore";
import { useCostPriceStore } from "../../../../../../stores/appCostPriceStore";
import { useSourceRestaurantDataStore } from "../../../../../../stores/appSourceRestaurantDataStore";

interface SourceRestaurantFilterProps {
    currentRestaurantId: string;
    /**
     * When true, render only the dropdown button + inline status (no own bordered
     * row) so the caller can place it on the same line as other filters. Default
     * (false) keeps the standalone bordered row used by the Custom/Sub-recipe tabs.
     */
    inline?: boolean;
}

/**
 * Shared restaurant multi-select for the add-ingredient picker's data tabs
 * (From Orders / Custom Ingredients / Sub recipes). The selection lives in
 * `appCostPriceStore` so it is shared across the three tabs and survives tab
 * switches. Default selection = [currentRestaurantId] (seeded by AddIngredients).
 *
 * Reads per-restaurant loading/error state from `appSourceRestaurantDataStore`
 * to surface inline feedback while a sibling's data loads.
 */
const SourceRestaurantFilter: React.FC<SourceRestaurantFilterProps> = ({ currentRestaurantId, inline = false }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const restaurants = useRestaurantStore(s => s.restaurants);
    const selectedIds = useCostPriceStore(s => s.addIngredientSourceRestaurantIds);
    const setSelectedIds = useCostPriceStore(s => s.setAddIngredientSourceRestaurantIds);
    const loadingByRestaurant = useSourceRestaurantDataStore(s => s.loadingByRestaurant);
    const errorByRestaurant = useSourceRestaurantDataStore(s => s.errorByRestaurant);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // Only show the selector when the user has access to more than one restaurant.
    if (restaurants.length <= 1) return null;

    // Sort with the current restaurant first.
    const options = [...restaurants]
        .sort((a, b) => (a.id === currentRestaurantId ? -1 : b.id === currentRestaurantId ? 1 : 0))
        .map(r => ({ id: r.id, name: r.name }));

    const siblingsSelected = selectedIds.filter(id => id !== currentRestaurantId);
    const anyLoading = siblingsSelected.some(id => loadingByRestaurant[id]);
    const anyError = siblingsSelected.some(id => errorByRestaurant[id]);

    const inner = (
        <>
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className={`flex items-center gap-2 px-3 h-9 rounded-lg border text-[14px] font-medium transition-colors ${
                        siblingsSelected.length > 0
                            ? 'border-darkGreenColor dark:border-darkGreenColor-dark bg-green-50 dark:bg-green-900/20 text-darkGreenColor dark:text-darkGreenColor-dark'
                            : 'border-borderGrey dark:border-borderGrey-dark bg-backgroundSecondary dark:bg-backgroundSecondary-dark text-textSecondary dark:text-textSecondary-dark'
                    }`}
                >
                    <span>{t('costPrice.addIngredient.sourceRestaurants')}</span>
                    {selectedIds.length > 0 && (
                        <span className="bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white text-[12px] px-1.5 rounded-full">
                            {selectedIds.length}
                        </span>
                    )}
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {open && (
                    <div className="absolute top-full left-0 mt-1 z-20 bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded-lg shadow-lg p-3 min-w-[280px]">
                        <MultiSelectFilter
                            label={t('costPrice.addIngredient.sourceRestaurants')}
                            selectedValues={selectedIds}
                            onValuesChange={setSelectedIds}
                            availableOptions={options}
                            maxHeight="max-h-[200px]"
                        />
                    </div>
                )}
            </div>

            {anyLoading && (
                <span className="flex items-center gap-1 text-[13px] text-textSecondary dark:text-textSecondary-dark">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t('costPrice.addIngredient.loadingRestaurants')}
                </span>
            )}
            {!anyLoading && anyError && (
                <span className="text-[13px] text-red-500 dark:text-textRed-dark">
                    {t('costPrice.addIngredient.errorLoadingRestaurant')}
                </span>
            )}
        </>
    );

    // Inline: hand back the bare button group so the caller can share one filter row.
    if (inline) return inner;

    return (
        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-borderGrey dark:border-borderGrey-dark">
            {inner}
        </div>
    );
};

export default SourceRestaurantFilter;
